import React from 'react';
import { View, Pressable, ViewStyle, Animated } from 'react-native';
import { useTheme } from '../themes';

export interface CardProps {
  children: React.ReactNode;
  variant?: 'default' | 'elevated' | 'outlined' | 'glass' | 'surface';
  elevation?: 0 | 1 | 2 | 3 | 4 | 5;
  padding?: keyof typeof import('../tokens').spacingTokens.spacing;
  borderRadius?: keyof typeof import('../tokens').spacingTokens.borderRadius;
  onPress?: () => void;
  disabled?: boolean;
  testID?: string | undefined;
  style?: ViewStyle;
  pressAnimation?: boolean;
  dotPattern?: boolean;
}

export function Card({
  children,
  variant = 'default',
  elevation,
  padding = 4,
  borderRadius = 'md',
  onPress,
  disabled = false,
  testID,
  style,
  pressAnimation = true,
  dotPattern = false,
}: CardProps) {
  const { theme } = useTheme();
  const animatedValue = React.useRef(new Animated.Value(1)).current;

  const Component = onPress ? Pressable : View;

  // Get background color based on variant and theme
  const getBackgroundColor = () => {
    switch (variant) {
      case 'elevated':
        return theme.colors.surface.elevated;
      case 'outlined':
        return theme.colors.surface.primary;
      case 'glass':
        return theme.colors.surface.overlay;
      case 'surface':
        return theme.colors.surface.secondary;
      case 'default':
      default:
        return theme.colors.surface.primary;
    }
  };

  // Get border style based on variant
  const getBorderStyle = () => {
    if (variant === 'outlined') {
      return {
        borderWidth: 1,
        borderColor: theme.colors.border.primary,
      };
    }
    return {};
  };

  // Get shadow style based on variant and elevation
  const getShadowStyle = () => {
    let shadowElevation = elevation;

    if (!shadowElevation) {
      switch (variant) {
        case 'elevated':
          shadowElevation = 3;
          break;
        case 'glass':
          shadowElevation = 2;
          break;
        case 'default':
          shadowElevation = 1;
          break;
        default:
          shadowElevation = 0;
      }
    }

    return theme.spacing.elevation[shadowElevation];
  };

  // Handle press animations
  const handlePressIn = () => {
    if (pressAnimation && !disabled) {
      Animated.spring(animatedValue, {
        toValue: 0.98,
        useNativeDriver: true,
        tension: 300,
        friction: 20,
      }).start();
    }
  };

  const handlePressOut = () => {
    if (pressAnimation && !disabled) {
      Animated.spring(animatedValue, {
        toValue: 1,
        useNativeDriver: true,
        tension: 300,
        friction: 20,
      }).start();
    }
  };

  const cardStyle = {
    borderRadius: theme.spacing.borderRadius[borderRadius],
    padding: theme.spacing.spacing[padding],
    backgroundColor: getBackgroundColor(),
    opacity: disabled ? 0.5 : 1,
    ...getBorderStyle(),
    ...getShadowStyle(),
  };

  const animatedStyle =
    pressAnimation && onPress
      ? {
          transform: [{ scale: animatedValue }],
        }
      : {};

  return (
    <Animated.View style={[cardStyle, animatedStyle, style]}>
      <Component
        style={{ flex: 1 }}
        {...(onPress
          ? {
              onPress,
              onPressIn: handlePressIn,
              onPressOut: handlePressOut,
              disabled,
            }
          : {})}
        testID={testID}
      >
        {dotPattern && <DotPattern />}
        {children}
      </Component>
    </Animated.View>
  );
}

// Nothing-inspired dot pattern component
function DotPattern() {
  const { theme } = useTheme();

  return (
    <View
      style={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        opacity: 0.03,
      }}
      pointerEvents='none'
    >
      {/* Create a subtle dot pattern using multiple small circles */}
      {Array.from({ length: 100 }).map((_, index) => (
        <View
          key={index}
          style={{
            position: 'absolute',
            width: 1,
            height: 1,
            backgroundColor: theme.colors.text.primary,
            borderRadius: 0.5,
            left: (index % 10) * 20 + 10,
            top: Math.floor(index / 10) * 20 + 10,
          }}
        />
      ))}
    </View>
  );
}

export interface CardHeaderProps {
  children: React.ReactNode;
  style?: ViewStyle;
}

export function CardHeader({ children, style }: CardHeaderProps) {
  return <View style={[{ marginBottom: 12 }, style]}>{children}</View>;
}

export interface CardBodyProps {
  children: React.ReactNode;
  style?: ViewStyle;
}

export function CardBody({ children, style }: CardBodyProps) {
  return <View style={[{ flex: 1 }, style]}>{children}</View>;
}

export interface CardFooterProps {
  children: React.ReactNode;
  style?: ViewStyle;
}

export function CardFooter({ children, style }: CardFooterProps) {
  return (
    <View
      style={[
        {
          marginTop: 12,
          flexDirection: 'row',
          justifyContent: 'flex-end',
          alignItems: 'center',
        },
        style,
      ]}
    >
      {children}
    </View>
  );
}
